// src/utils/translationCheck.js
// Prüft eine frei eingetippte Übersetzung gegen die Musterlösung.
// Großzügig bei Kleinkram (Groß-/Kleinschreibung, Satzzeichen, ¿¡, Apostroph-Varianten), mit
// Hinweis bei fehlenden Akzenten und kleinen Tippfehlern, im Französischen auch bei fehlender
// Verkürzung (je ai → j'ai) und fehlendem „ne“ der Verneinung (je sais pas). Was nicht passt, zeigt der Wortvergleich
// (diffWords); ob es trotzdem stimmt, entscheidet der Nutzer („War auch richtig“).

// Ergebnis, vom besten zum schlechtesten
//   correct: stimmt (bis auf Groß-/Kleinschreibung und Satzzeichen)
//   accent:  stimmt bis auf Akzente/Umlaute (está/esta, für/fur, ß/ss)
//   elision: Französisch, stimmt bis auf die Verkürzung vor Vokal (je ai/j'ai, l eau/l'eau)
//   ne:      Französisch, stimmt bis auf das fehlende „ne“ der Verneinung (je sais pas)
//   typo:    kleiner Tippfehler (wenige Buchstaben)
//   order:   dieselben Wörter, andere Reihenfolge
//   wrong:   passt nicht
const STATUS_RANK = { correct: 0, accent: 1, elision: 2, ne: 3, typo: 4, order: 5, wrong: 6 };

// Diese Ergebnisse zählen als gelöst
export function isSolved(status) {
  return (
    status === "correct" ||
    status === "accent" ||
    status === "elision" ||
    status === "ne" ||
    status === "typo"
  );
}

// Spanische Subjektpronomen dürfen fehlen oder zusätzlich dastehen („Tengo“ = „Yo tengo“)
const OPTIONAL_LEADING_WORDS = {
  es: new Set([
    "yo",
    "tú",
    "él", // nur mit Akzent, sonst wäre der Artikel „el“ gemeint
    "ella",
    "nosotros",
    "nosotras",
    "vosotros",
    "vosotras",
    "ellos",
    "ellas",
    "usted",
    "ustedes",
  ]),
};

// Französisch: Wörter, die vor Vokal oder h verkürzt werden (je ai → j'ai). Auch ohne Apostroph
// getippt („l eau“) zählt das Wort als die verkürzte Form. Subjektpronomen sind Pflicht (keine
// Einträge in OPTIONAL_LEADING_WORDS).
const FR_ELISION = {
  je: "j'", j: "j'",
  me: "m'", m: "m'",
  te: "t'", t: "t'",
  se: "s'", s: "s'",
  ne: "n'", n: "n'",
  de: "d'", d: "d'",
  le: "l'", la: "l'", l: "l'",
  que: "qu'", qu: "qu'",
  ce: "c'", c: "c'",
};
const FR_VOWEL_START = /^[aeiouyhàâäéèêëîïôöùûüœæ]/;

/**
 * Einheitliche Form für den Vergleich: Verkürzungen getrennt und überall vor Vokal/h gesetzt.
 * Erwartet normalizeText-Ausgabe. „je ai“, „j ai“ und „j'ai“ → „j' ai“. Beim „h aspiré“
 * (le héros) verkürzt sie fälschlich, aber auf beiden Seiten gleich.
 */
export function canonicalFrenchElision(text) {
  const words = text.replace(/'/g, "' ").split(" ").filter(Boolean);
  return words
    .map((w, i) => {
      const next = words[i + 1];
      if (!next || !FR_VOWEL_START.test(next)) return w;
      return FR_ELISION[w.replace(/'$/, "")] || w;
    })
    .join(" ");
}

// „ne“ der Verneinung, auch verkürzt (je n'ai pas → je ai pas); erwartet canonicalFrenchElision-Ausgabe
function withoutFrenchNe(text) {
  return text
    .split(" ")
    .filter((w) => w !== "ne" && w !== "n'")
    .join(" ");
}

// Spanische Objekt-/Reflexivpronomen dürfen vor dem Verb stehen oder am Infinitiv/Gerundium
// hängen: „Lo quiero hacer“ = „Quiero hacerlo“, „Lo estoy haciendo“ = „Estoy haciéndolo“.
const ES_CLITICS = ["les", "los", "las", "nos", "me", "te", "se", "os", "lo", "la", "le"];
const ES_CLITIC_SET = new Set(ES_CLITICS);
const ES_CLITIC_GROUP = `(${ES_CLITICS.join("|")})`;
// Infinitiv oder Gerundium mit 1–2 angehängten Pronomen (hacerlo, decírselo, irme)
const ES_ATTACHED = new RegExp(`^(.*(?:ar|er|ir|ando|iendo|yendo))${ES_CLITIC_GROUP}${ES_CLITIC_GROUP}?$`);
const ES_INFINITIVE = /(ar|er|ir)$/;
const ES_GERUND = /(ando|iendo|yendo)$/;
// Nur bei diesen Verben darf das Pronomen vor das konjugierte Verb wandern (Formen ohne Akut,
// erkannt am Stamm). `link`: Wort zwischen Verb und Infinitiv; `gerund`: Gerundium statt Infinitiv.
// Bei anderen Verben ändert die Stellung den Sinn oder ist falsch: „me gusta comer“ ≠ „gusta comerme“.
const ES_CLIMBING_VERBS = [
  { re: /^(quier|quer|quis|pued|pod|pud|deb|sab|sup|sep|suel|soli|sole|intent|necesit|piens|pens|prefier|prefer|prefir|dese)/ },
  { re: /^(voy|vas|va|vamos|vais|van|iba\w*|ire\w*|iri\w*|fue\w*|fui\w*|vay\w+)$/, link: "a" },
  { re: /^(teng|tien|tene|teni|tuv|tendr)/, link: "que" },
  { re: /^(acab|dej|termin|trat)/, link: "de" },
  { re: /^(empiez|empez|empec|comienz|comenz|comenc|vuelv|volv)/, link: "a" },
  { re: /^(est|sig|segu)/, gerund: true },
];

// Passt „verb (link) nonfinite“ zu einem Verb, bei dem das Pronomen wandern darf?
function climbingMatch(verb, next, after) {
  const plain = withoutAcute(verb);
  for (const { re, link, gerund } of ES_CLIMBING_VERBS) {
    if (!re.test(plain)) continue;
    if (link && next !== link) continue;
    const nonfinite = link ? after : next;
    if (!nonfinite || nonfinite.includes("+")) continue;
    if ((gerund ? ES_GERUND : ES_INFINITIVE).test(withoutAcute(nonfinite))) return { link, nonfinite };
  }
  return null;
}

/**
 * Fehlt einer angehängten Form der nötige Akzent? Gerundium + Pronomen (haciéndolo) und
 * Infinitiv + zwei Pronomen (decírselo) brauchen einen, Infinitiv + eins (hacerlo) nicht.
 */
function missesCliticAccent(text) {
  return text.split(" ").some((w) => {
    if (w !== withoutAcute(w)) return false;
    const m = w.match(ES_ATTACHED);
    return Boolean(m && (ES_GERUND.test(m[1]) || m[3]));
  });
}

// nur den Akut weg (haciéndolo → haciendolo), ñ bleibt
function withoutAcute(word) {
  return word.normalize("NFD").replace(/\u0301/g, "").normalize("NFC");
}

/**
 * Einheitliche Form für den Vergleich: Pronomen hängen am Infinitiv/Gerundium, als „hacer+lo“.
 * Erwartet normalizeText-Ausgabe. „lo quiero hacer“ und „quiero hacerlo“ → „quiero hacer+lo“.
 */
export function canonicalSpanishClitics(text) {
  const words = text.split(" ").map((w) => {
    const m = withoutAcute(w).match(ES_ATTACHED);
    return m ? [m[1], m[2], m[3]].filter(Boolean).join("+") : w;
  });
  const out = [];
  let i = 0;
  while (i < words.length) {
    let k = 0;
    while (k < 2 && ES_CLITIC_SET.has(words[i + k])) k++;
    const verb = words[i + k];
    const match =
      k > 0 && verb && !ES_CLITIC_SET.has(verb)
        ? climbingMatch(verb, words[i + k + 1], words[i + k + 2])
        : null;
    if (match) {
      out.push(verb);
      if (match.link) out.push(match.link);
      out.push([withoutAcute(match.nonfinite), ...words.slice(i, i + k)].join("+"));
      i += k + (match.link ? 3 : 2);
    } else {
      out.push(words[i]);
      i++;
    }
  }
  return out.join(" ");
}

/** Kleinbuchstaben, Satzzeichen weg, einheitliche Leerzeichen. Akzente bleiben. */
export function normalizeText(text) {
  return String(text || "")
    .normalize("NFC")
    .toLowerCase()
    .replace(/[’‘`´ʼ′]/g, "'")
    .replace(/[.,;:!?¡¿"“”„«»()[\]…–—-]/g, " ")
    .replace(/\s+/g, " ")
    .replace(/ ?' ?/g, "'")
    .trim();
}

/** Wie normalizeText, zusätzlich ohne Akzente/Umlaute (á → a, ü → u, ñ → n, ç → c, ß → ss, œ → oe). */
export function foldText(text) {
  return normalizeText(text)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ß/g, "ss")
    .replace(/œ/g, "oe")
    .replace(/æ/g, "ae");
}

export function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const curr = [i];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    prev = curr;
  }
  return prev[b.length];
}

// Tippfehler: höchstens 1 falscher Buchstabe pro Wort, in Wörtern ab 4 Buchstaben,
// und das Wortende stimmt. Endungen (tengo/tenga, habla/hablas, el/ella) sind meist
// Grammatik und keine Tippfehler. Höchstens 1 solches Wort je angefangene 6 Wörter.
function isTypoOnly(answer, solution) {
  const a = answer.split(" ");
  const s = solution.split(" ");
  if (a.length !== s.length) return false;
  let typos = 0;
  for (let i = 0; i < s.length; i++) {
    if (a[i] === s[i]) continue;
    const ok =
      s[i].length >= 4 &&
      a[i].slice(-1) === s[i].slice(-1) &&
      levenshtein(a[i], s[i]) === 1;
    if (!ok) return false;
    typos++;
  }
  return typos <= Math.ceil(s.length / 6);
}

function sortedWords(text) {
  return text.split(" ").filter(Boolean).sort().join(" ");
}

function compareOne(answer, solution) {
  if (normalizeText(answer) === normalizeText(solution)) return "correct";

  const a = foldText(answer);
  const s = foldText(solution);
  if (a === s) return "accent";
  if (isTypoOnly(a, s)) return "typo";
  if (sortedWords(a) === sortedWords(s)) return "order";
  return "wrong";
}

// Text ohne erstes Wort, falls das ein weglassbares Pronomen ist (sonst null)
function withoutOptionalLeadingWord(text, lang) {
  const optional = OPTIONAL_LEADING_WORDS[lang];
  if (!optional) return null;
  const words = normalizeText(text).split(" ");
  if (words.length < 2) return null;
  return optional.has(words[0]) ? words.slice(1).join(" ") : null;
}

/**
 * Vergleicht `answer` mit `solution`. `lang`: Sprache der Antwort (z. B. "es"),
 * `acceptedVariants`: weitere Antworten, die schon einmal als richtig akzeptiert wurden.
 * Rückgabe: "correct" | "accent" | "elision" | "ne" | "typo" | "order" | "wrong"
 * (oder "empty" ohne Eingabe)
 */
export function checkTranslation(answer, solution, { lang, acceptedVariants = [] } = {}) {
  if (!normalizeText(answer)) return "empty";

  const normalizedAnswer = normalizeText(answer);
  if (acceptedVariants.some((v) => normalizeText(v) === normalizedAnswer)) {
    return "correct";
  }

  // Pronomen weglassen ist erlaubt, ein anderes Pronomen einsetzen nicht:
  // deshalb nie beide Seiten gleichzeitig kürzen.
  const pairs = [[answer, solution]];
  const solutionShort = withoutOptionalLeadingWord(solution, lang);
  const answerShort = withoutOptionalLeadingWord(answer, lang);
  if (solutionShort) pairs.push([answer, solutionShort]);
  if (answerShort) pairs.push([answerShort, solution]);

  let best = "wrong";
  for (const [a, s] of pairs) {
    const status = compareOne(a, s);
    if (STATUS_RANK[status] < STATUS_RANK[best]) best = status;
  }

  // Andere Stellung der Pronomen nur, wenn es so nicht passt (sonst gingen Akzent-Hinweise
  // wie „haciendolo“ statt „haciéndolo“ verloren)
  if (lang === "es" && !isSolved(best)) {
    for (const [a, s] of pairs) {
      let status = compareOne(
        canonicalSpanishClitics(normalizeText(a)),
        canonicalSpanishClitics(normalizeText(s))
      );
      // „Estoy haciendolo“ für „Lo estoy haciendo“: richtig, aber mit Akzent-Hinweis
      if (status === "correct" && missesCliticAccent(normalizeText(a))) status = "accent";
      if (STATUS_RANK[status] < STATUS_RANK[best]) best = status;
    }
  }

  // Französisch: Verkürzung und „ne“ nur prüfen, wenn es so nicht passt
  if (lang === "fr" && !isSolved(best)) {
    const a = canonicalFrenchElision(normalizeText(answer));
    const s = canonicalFrenchElision(normalizeText(solution));
    let status = compareOne(a, s);
    if (status === "correct") status = "elision";
    // „ne“ darf nur in der Antwort fehlen, die Lösung hat es (je sais pas für je ne sais pas)
    if (!isSolved(status) && withoutFrenchNe(a) === a) {
      const sNoNe = canonicalFrenchElision(withoutFrenchNe(s));
      if (sNoNe !== s) {
        const noNe = compareOne(a, sNoNe);
        if (isSolved(noNe)) status = "ne";
        else if (STATUS_RANK[noNe] < STATUS_RANK[status]) status = noNe;
      }
    }
    if (STATUS_RANK[status] < STATUS_RANK[best]) best = status;
  }
  return best;
}

/**
 * Wortvergleich für die Anzeige nach dem Prüfen (längste gemeinsame Wortfolge).
 * Wörter werden ohne Akzente verglichen, damit „esta“ zu „está“ passt.
 * Rückgabe:
 *   answer:   [{ text, status: "ok" | "accent" | "extra" }]   (extra = passt nicht zur Lösung)
 *   solution: [{ text, status: "ok" | "accent" | "missing" }] (missing = fehlt in der Antwort)
 */
export function diffWords(answer, solution) {
  const answerWords = String(answer || "").split(/\s+/).filter(Boolean);
  const solutionWords = String(solution || "").split(/\s+/).filter(Boolean);
  const aKeys = answerWords.map(foldText);
  const sKeys = solutionWords.map(foldText);

  // Tabelle der längsten gemeinsamen Teilfolge, von hinten aufgebaut
  const n = aKeys.length;
  const m = sKeys.length;
  const lcs = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] =
        aKeys[i] && aKeys[i] === sKeys[j]
          ? lcs[i + 1][j + 1] + 1
          : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const answerOut = answerWords.map((text) => ({ text, status: "extra" }));
  const solutionOut = solutionWords.map((text) => ({ text, status: "missing" }));

  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (aKeys[i] && aKeys[i] === sKeys[j]) {
      const sameAccents =
        normalizeText(answerWords[i]) === normalizeText(solutionWords[j]);
      const status = sameAccents ? "ok" : "accent";
      answerOut[i].status = status;
      solutionOut[j].status = status;
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      i++;
    } else {
      j++;
    }
  }

  // Reine Satzzeichen (z. B. ein einzelnes „?“) nie als Fehler markieren
  for (const list of [answerOut, solutionOut]) {
    for (const word of list) {
      if (!foldText(word.text)) word.status = "ok";
    }
  }

  return { answer: answerOut, solution: solutionOut };
}
